import type { Session } from '@supabase/supabase-js';

import { supabase } from '../../../shared/api/supabase/client.js';
import {
  AppErrorException,
  appErrorException,
  toAppError,
  type AppError,
} from '../../../shared/lib/errors.js';

export type SignInResult = { ok: true } | { ok: false; error: AppError };

/**
 * Email + password only. Public signup is disabled on the server (spec v2 19, B7):
 * accounts are created by the owner, so there is no register screen by design.
 */
export async function signIn(email: string, password: string): Promise<SignInResult> {
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  return error ? { ok: false, error: toAppError(error) } : { ok: true };
}

export async function signOut(): Promise<void> {
  await supabase.auth.signOut();
}

export async function getSession(): Promise<Session | null> {
  const { data } = await supabase.auth.getSession();
  return data.session;
}

export function onAuthStateChange(handler: (session: Session | null) => void): () => void {
  const { data } = supabase.auth.onAuthStateChange((_event, session) => {
    handler(session);
  });
  return () => {
    data.subscription.unsubscribe();
  };
}

/**
 * Changes the signed-in person's password.
 *
 * The current password is verified by signing in with it first, and that is deliberate on
 * two counts. A phone left unlocked must not be enough to lock its owner out of the
 * household's books. And a fresh sign-in is what GoTrue's "secure password change" setting
 * accepts in place of an email OTP (`reauthenticate()` + nonce) — the email-dependent flow
 * this app avoids by design (decision B7, spec v2 19). `current_password` on `updateUser` is
 * not used: it only works behind a GoTrue flag that `supabase/config.toml` does not expose.
 */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const { data } = await supabase.auth.getSession();
  const email = data.session?.user.email;
  if (!email) {
    throw appErrorException(new Error('unauthorized'));
  }

  const verified = await signIn(email, currentPassword);
  if (!verified.ok) {
    // GoTrue answers "invalid login credentials"; what the person got wrong is the CURRENT
    // password, so say that. Anything else (network…) is passed through as it is.
    throw new AppErrorException(
      verified.error.kind === 'unauthorized'
        ? { kind: 'validation', message: 'Mật khẩu hiện tại chưa đúng.' }
        : verified.error,
    );
  }

  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw appErrorException(error);
}
